/**
 * Build canvas nodes for assets placed from the Assets panel.
 *
 * One builder serves both "Place on stage" (a single asset, anchored at the
 * viewport centre) and "Place on canvas" for a multi-selection (a grid around
 * the viewport centre), so a batch placement produces exactly the node type and
 * data shape a single placement does.
 *
 * Sizes come from the node registry (`getNodeDimensions` /
 * `getOutputNodeDimensions`), never from literals: hardcoded spawn sizes are
 * how nodes ended up at sizes nothing else in the editor agreed with (see
 * `nodeDimensionRepair.ts`).
 */

import type { Node } from '@xyflow/react';
import { getNodeDimensions, getOutputNodeDimensions } from '../config/nodeRegistry';

/** The slice of an Assets-panel file this module needs. */
export interface StageableAsset {
  id: string;
  name: string;
  type: string;
  url?: string;
  previewUrl?: string;
}

export interface FlowPoint {
  x: number;
  y: number;
}

/** A screen-space rectangle (the subset of DOMRect this module reads). */
export interface ScreenRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Where placed assets go, in flow coordinates: the centre of the part of the
 * canvas the palette overlay does not cover, and the leftmost x a node may
 * start at so it is not hidden under the overlay.
 */
export interface AssetPlacementFrame {
  center: FlowPoint;
  minX: number | null;
}

/** Breathing room between the palette overlay and a placed node, in screen px. */
export const OVERLAY_CLEARANCE = 24;

/** Below this much uncovered canvas width the overlay is treated as full-screen (mobile) and ignored. */
const MIN_UNCOVERED_WIDTH = 240;

/**
 * The Assets panel stays open while assets are placed, and it is an opaque
 * fixed overlay on the left of the canvas (sidebar + 660px panel). Centring on
 * the full canvas put new nodes partly or wholly under it. This centres on the
 * uncovered part instead and returns its left edge as a clamp.
 *
 * An overlay that is not docked on the left, or that leaves too little canvas
 * (the mobile bottom bar / full-screen sheet), is ignored.
 */
export function computeAssetPlacementFrame(
  canvas: ScreenRect,
  overlay: ScreenRect | null,
  toFlow: (point: FlowPoint) => FlowPoint
): AssetPlacementFrame {
  const centerY = (canvas.top + canvas.bottom) / 2;
  let left = canvas.left;
  const docksLeft = overlay
    && overlay.left <= canvas.left + 1
    && overlay.right > canvas.left
    && overlay.top <= centerY
    && overlay.bottom >= centerY;
  if (overlay && docksLeft && canvas.right - (overlay.right + OVERLAY_CLEARANCE) >= MIN_UNCOVERED_WIDTH) {
    left = overlay.right + OVERLAY_CLEARANCE;
  }
  const center = toFlow({ x: (left + canvas.right) / 2, y: centerY });
  const minX = left > canvas.left ? toFlow({ x: left, y: centerY }).x : null;
  return { center, minX };
}

/** Height of a node's header; a single placement centres the header on the anchor. */
const HEADER_HEIGHT = 44;

/** Space between cells when several assets are laid out as a grid. */
export const ASSET_GRID_GAP = 40;

type AssetKind = 'image' | 'video' | 'audio' | 'text';

function assetKind(file: StageableAsset): AssetKind | null {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  if (file.type.startsWith('text/') || file.name.endsWith('.txt') || file.name.endsWith('.md')) return 'text';
  return null;
}

/** Whether the asset has a node it can be placed as. */
export function isStageableAsset(file: StageableAsset): boolean {
  return assetKind(file) !== null;
}

/**
 * The node for one asset, with its top-left at `position`. Returns null for a
 * file type that has no canvas node.
 */
export function buildAssetStageNode(
  file: StageableAsset,
  position: FlowPoint,
  makeId: (prefix: string) => string
): Node | null {
  const fileUrl = file.url || file.previewUrl || '';

  switch (assetKind(file)) {
    case 'image': {
      const { width, height } = getNodeDimensions('imagePanel');
      return {
        id: makeId('imagePanel'),
        type: 'imagePanel',
        position,
        width,
        height,
        data: {
          imageUrl: fileUrl,
          fileName: file.name,
          label: `Image: ${file.name}`,
          fileId: file.id,
          fileType: file.type
        }
      };
    }
    case 'video':
    case 'audio': {
      const kind = assetKind(file) as 'video' | 'audio';
      const { width, height } = getOutputNodeDimensions(kind);
      return {
        id: makeId('customOutput'),
        type: 'customOutput',
        position,
        width,
        height,
        data: {
          pages: [{ type: kind, content: fileUrl }],
          currentPageIndex: 0,
          output: fileUrl,
          type: kind,
          fileName: file.name,
          fileId: file.id,
          onPageChange: () => {},
          onContentChange: () => {},
          onDelete: () => {},
          onDuplicate: () => {},
        }
      };
    }
    case 'text': {
      const { width, height } = getNodeDimensions('documentPanel');
      return {
        id: makeId('documentPanel'),
        type: 'documentPanel',
        position,
        width,
        height,
        data: {
          content: '',  // Content will need to be loaded
          fileName: file.name,
          fileId: file.id,
          fileType: file.type,
          fileUrl: fileUrl,
          pages: [{
            content: `Loading ${file.name}...`,
            context: null,
            type: 'text' as const,
            timestamp: new Date().toISOString()
          }],
          currentPageIndex: 0
        }
      };
    }
    default:
      return null;
  }
}

/** A node's rectangle in flow coordinates. */
export interface FlowBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface NodeSize {
  width: number;
  height: number;
}

/** How the packed block sits on its anchor. */
export type AssetAnchorMode =
  /** One node: centred horizontally on the anchor, header on the anchor line. */
  | 'header'
  /** A batch: the block's centre on the anchor. */
  | 'center';

export interface AssetLayoutOptions {
  /** Leftmost x a node may start at (the right edge of the open Assets panel). */
  minX?: number | null;
  /** Boxes of nodes already on the canvas; the block moves until it clears all of them. */
  obstacles?: FlowBox[];
  anchor?: AssetAnchorMode;
  gap?: number;
}

/**
 * The size a node actually occupies on the canvas.
 *
 * `node.height` is only what the node asked for. The rendered height is set by
 * CSS: `.react-flow__node { height: auto !important; min-height: 448px }` in
 * `styles/components/_ui-components.css` overrides the inline height, so an
 * Image Container that asks for 300 renders at least 448 tall, and taller for
 * a portrait image. The editor fills `node.measured` from the rendered
 * element (see `renderedNode` in CreativeNodeFlow); that is the only number
 * layout can trust. Before the node has rendered, the requested size is the
 * best estimate.
 */
export function nodeSize(node: Node): NodeSize {
  return {
    width: node.measured?.width ?? (node.width as number | undefined) ?? 0,
    height: node.measured?.height ?? (node.height as number | undefined) ?? 0,
  };
}

/** Whether React Flow has measured the rendered node yet. */
export function isNodeMeasured(node: Node): boolean {
  return !!node.measured?.width && !!node.measured?.height;
}

export function nodeBox(node: Node): FlowBox {
  return { x: node.position.x, y: node.position.y, ...nodeSize(node) };
}

/** Boxes of the visible nodes, minus `exclude` — what a placement must stay clear of. */
export function obstacleBoxes(nodes: Node[], exclude?: Set<string>): FlowBox[] {
  return nodes.filter(n => !n.hidden && !exclude?.has(n.id)).map(nodeBox);
}

/** Whether two boxes come closer than `gap` to each other. */
function tooClose(a: FlowBox, b: FlowBox, gap: number): boolean {
  return a.x < b.x + b.width + gap
    && b.x < a.x + a.width + gap
    && a.y < b.y + b.height + gap
    && b.y < a.y + a.height + gap;
}

/**
 * The top-left for a `width` x `height` block nearest to `preferred` that keeps
 * `gap` clear of every obstacle and does not start left of `minX`.
 *
 * Candidates are the preferred spot plus every position flush (with the gap)
 * against an obstacle edge; the nearest free one wins. One always exists: the
 * column right of the rightmost obstacle is free.
 */
export function findFreeOrigin(
  preferred: FlowPoint,
  width: number,
  height: number,
  obstacles: FlowBox[],
  minX: number | null | undefined,
  gap: number = ASSET_GRID_GAP
): FlowPoint {
  const floorX = minX == null ? -Infinity : minX;
  const start = { x: Math.max(preferred.x, floorX), y: preferred.y };
  const relevant = obstacles.filter(o => o.width > 0 && o.height > 0);
  const free = (p: FlowPoint) => !relevant.some(o => tooClose({ x: p.x, y: p.y, width, height }, o, gap));
  if (free(start)) return start;

  const xs = new Set<number>([start.x]);
  const ys = new Set<number>([start.y]);
  for (const o of relevant) {
    xs.add(o.x + o.width + gap);
    xs.add(o.x - gap - width);
    ys.add(o.y + o.height + gap);
    ys.add(o.y - gap - height);
  }
  const candidates: { p: FlowPoint; d: number }[] = [];
  for (const x of xs) {
    if (x < floorX) continue;
    for (const y of ys) {
      candidates.push({ p: { x, y }, d: Math.hypot(x - start.x, y - start.y) });
    }
  }
  candidates.sort((a, b) => a.d - b.d || a.p.y - b.p.y || a.p.x - b.p.x);
  for (const c of candidates) {
    if (free(c.p)) return c.p;
  }
  // Unreachable (right of every obstacle is free); kept as a total fallback.
  return { x: Math.max(start.x, ...relevant.map(o => o.x + o.width + gap)), y: start.y };
}

/**
 * Positions (top-left, in input order) for nodes of the given sizes, packed as
 * a near-square grid. Each column is as wide as its widest node and each row as
 * tall as its tallest, so nodes of different heights never overlap. The block
 * is anchored per `anchor`, kept right of `minX`, then moved to the nearest
 * spot that clears every obstacle.
 */
export function packAssetLayout(
  sizes: NodeSize[],
  anchorPoint: FlowPoint,
  options: AssetLayoutOptions = {}
): FlowPoint[] {
  if (sizes.length === 0) return [];
  const { minX, obstacles = [], anchor = 'center', gap = ASSET_GRID_GAP } = options;

  const columns = Math.ceil(Math.sqrt(sizes.length));
  const rows = Math.ceil(sizes.length / columns);
  const colWidths: number[] = Array.from({ length: columns }, () => 0);
  const rowHeights: number[] = Array.from({ length: rows }, () => 0);
  sizes.forEach((s, i) => {
    const c = i % columns;
    const r = Math.floor(i / columns);
    colWidths[c] = Math.max(colWidths[c], s.width);
    rowHeights[r] = Math.max(rowHeights[r], s.height);
  });
  const colX: number[] = [];
  colWidths.reduce((x, w, c) => { colX[c] = x; return x + w + gap; }, 0);
  const rowY: number[] = [];
  rowHeights.reduce((y, h, r) => { rowY[r] = y; return y + h + gap; }, 0);
  const blockWidth = colX[columns - 1] + colWidths[columns - 1];
  const blockHeight = rowY[rows - 1] + rowHeights[rows - 1];

  const preferred = anchor === 'header'
    ? { x: anchorPoint.x - blockWidth / 2, y: anchorPoint.y - HEADER_HEIGHT / 2 }
    : { x: anchorPoint.x - blockWidth / 2, y: anchorPoint.y - blockHeight / 2 };
  const origin = findFreeOrigin(preferred, blockWidth, blockHeight, obstacles, minX, gap);

  return sizes.map((_, i) => ({
    x: origin.x + colX[i % columns],
    y: origin.y + rowY[Math.floor(i / columns)],
  }));
}

/**
 * Re-pack an already placed batch with what React Flow measured, so the layout
 * follows the rendered heights rather than the requested ones. Returns the new
 * position per node id. The same anchor and options as the original placement
 * give the same block, so running it again on unchanged sizes is a no-op.
 */
export function relayoutAssetBatch(
  batch: Node[],
  anchorPoint: FlowPoint,
  options: AssetLayoutOptions = {}
): Map<string, FlowPoint> {
  const positions = packAssetLayout(batch.map(nodeSize), anchorPoint, options);
  return new Map(batch.map((n, i) => [n.id, positions[i]]));
}

/**
 * Single placement: the node is centred horizontally on `anchor` with its
 * header on the anchor's line (the behaviour "Place on stage" always had),
 * pushed right if it would start left of `minX`, and moved off any existing
 * node in `obstacles`.
 */
export function buildAssetStageNodeAt(
  file: StageableAsset,
  anchor: FlowPoint,
  makeId: (prefix: string) => string,
  minX?: number | null,
  obstacles: FlowBox[] = []
): Node | null {
  const node = buildAssetStageNode(file, { x: 0, y: 0 }, makeId);
  if (!node) return null;
  const [position] = packAssetLayout([nodeSize(node)], anchor, { minX, obstacles, anchor: 'header' });
  return { ...node, position };
}

/**
 * Several assets at once: one node per placeable asset, laid out as a
 * near-square grid centred on `center` (see `packAssetLayout`), right of
 * `minX` and clear of `obstacles`. Sizes here are the requested ones; the
 * editor re-packs with `relayoutAssetBatch` once React Flow has measured the
 * rendered nodes.
 * Assets with no canvas node are returned in `skipped`, in input order.
 */
export function buildAssetStageGrid(
  files: StageableAsset[],
  center: FlowPoint,
  makeId: (prefix: string) => string,
  minX?: number | null,
  obstacles: FlowBox[] = []
): { nodes: Node[]; skipped: StageableAsset[] } {
  const skipped: StageableAsset[] = [];
  const built: Node[] = [];
  for (const file of files) {
    const node = buildAssetStageNode(file, { x: 0, y: 0 }, makeId);
    if (node) built.push(node);
    else skipped.push(file);
  }
  const positions = packAssetLayout(built.map(nodeSize), center, { minX, obstacles });
  return { nodes: built.map((node, i) => ({ ...node, position: positions[i] })), skipped };
}

/**
 * How far to move the view (in flow units) so that `placed` is centred in
 * `visible`, or null when every placed box is already fully inside it.
 * Avoiding existing nodes can push a placement off screen; without this the
 * user sees nothing happen (and React Flow, which only renders visible nodes,
 * never mounts them to be measured).
 */
export function revealShift(placed: FlowBox[], visible: FlowBox): FlowPoint | null {
  if (placed.length === 0) return null;
  const left = Math.min(...placed.map(b => b.x));
  const top = Math.min(...placed.map(b => b.y));
  const right = Math.max(...placed.map(b => b.x + b.width));
  const bottom = Math.max(...placed.map(b => b.y + b.height));
  const inside = left >= visible.x && top >= visible.y
    && right <= visible.x + visible.width && bottom <= visible.y + visible.height;
  if (inside) return null;
  return {
    x: (left + right) / 2 - (visible.x + visible.width / 2),
    y: (top + bottom) / 2 - (visible.y + visible.height / 2),
  };
}
